/**
 * Field Update Context & Provider
 *
 * React context system for sharing field update information across dialog components.
 * Central hub for real-time synchronization, field-level updates, and visual indicators.
 * Integrates seamlessly with MinimalDataMapping system and useChangeDetection hook.
 *
 * 🔍 CORE FEATURES:
 * - Real-time field update tracking and propagation
 * - Context-based data sharing across component trees
 * - Integration with external update sources (Firebase, WebSocket, etc.)
 * - Performance optimized with memoization and selective updates
 * - Support for different form modes (view, edit, review)
 * - Automatic grouping and deduplication of field updates
 * - Visual indicator coordination for changed fields
 *
 * 🚀 BASIC USAGE:
 * ```tsx
 * <FieldUpdateProvider
 *   formData={formData}
 *   originalData={originalData}
 *   fieldUpdates={fieldUpdates}
 *   updateField={updateField}
 *   isDirty={isDirty}
 *   mode="edit"
 *   isRealtimeActive={true}
 * >
 *   <YourFormComponents />
 * </FieldUpdateProvider>
 * ```
 *
 * 🔧 CONSUMING THE CONTEXT:
 * ```tsx
 * const {
 *   formData,
 *   fieldUpdates,
 *   updateField,
 *   isFieldUpdated,
 *   getFieldValue
 * } = useFieldUpdateContext();
 * ```
 *
 * 🎯 INTEGRATION WITH OTHER SYSTEMS:
 * - Works with useChangeDetection for dirty state management
 * - Compatible with MinimalDataMapping transformations
 * - Supports UnsavedChangesDialog for user confirmations
 * - Enables FieldUpdateIndicator components for visual feedback
 *
 * @author Claude Code
 * @date 2025-09-22
 */

import React, { createContext, useContext, useCallback, useMemo } from 'react';
import {
  FieldUpdateEvent,
  FieldUpdateContextValue,
  FormDialogMode
} from '../types';
import { groupUpdatesByPrefix, getLatestUpdatesPerPath } from '../utils';

/**
 * Field Update Context
 */
const FieldUpdateContext = createContext<FieldUpdateContextValue | null>(null);

// Export the context for components that need direct access
export { FieldUpdateContext };

// Export the type for components that need type-only imports
export type { FieldUpdateContextValue };

/**
 * Field Update Context Props
 */
export interface FieldUpdateProviderProps<TModel = any> {
  /** Current form data */
  formData: TModel;

  /** Original form data */
  originalData: TModel;

  /** All field updates */
  fieldUpdates: FieldUpdateEvent[];

  /** Update field function */
  updateField: (path: string, value: any) => void;

  /** Whether form is dirty */
  isDirty: boolean;

  /** Current form mode */
  mode: FormDialogMode;

  /** Whether real-time sync is active */
  isRealtimeActive: boolean;

  /** Children to render */
  children: React.ReactNode;
}

/**
 * Field Update Provider Component
 */
export function FieldUpdateProvider<TModel>({
  formData,
  originalData,
  fieldUpdates,
  updateField,
  isDirty,
  mode,
  isRealtimeActive,
  children
}: FieldUpdateProviderProps<TModel>) {

  // Dismiss update function (for backward compatibility)
  const dismissUpdate = useCallback((updateId: string) => {
    // This could be implemented to remove updates from the list
    // For now, we'll just log it
    console.log('Dismissing update:', updateId);
  }, []);

  // Memoized context value to prevent unnecessary re-renders
  const contextValue = useMemo<FieldUpdateContextValue<TModel>>(() => ({
    formData,
    originalData,
    fieldUpdates,
    updates: fieldUpdates, // Alias for backward compatibility
    updateField,
    isDirty,
    mode,
    isRealtimeActive,
    dismissUpdate
  }), [
    formData,
    originalData,
    fieldUpdates,
    updateField,
    isDirty,
    mode,
    isRealtimeActive,
    dismissUpdate
  ]);

  return (
    <FieldUpdateContext.Provider value={contextValue}>
      {children}
    </FieldUpdateContext.Provider>
  );
}

/**
 * Hook to use field update context
 */
export function useFieldUpdateContext<TModel = any>(): FieldUpdateContextValue<TModel> {
  const context = useContext(FieldUpdateContext);

  if (!context) {
    throw new Error('useFieldUpdateContext must be used within a FieldUpdateProvider');
  }

  return context as FieldUpdateContextValue<TModel>;
}

/**
 * Hook to get updates for a specific field
 */
export function useFieldUpdates(fieldPath: string): {
  updates: FieldUpdateEvent[];
  latestUpdate: FieldUpdateEvent | null;
  hasUpdates: boolean;
  hasRecentUpdates: boolean;
} {
  const { fieldUpdates } = useFieldUpdateContext();

  return useMemo(() => {
    // Get all updates for this field path
    const fieldSpecificUpdates = fieldUpdates.filter(
      update => update.path === fieldPath
    );

    // Get latest update
    const latestUpdate = fieldSpecificUpdates.length > 0
      ? fieldSpecificUpdates[fieldSpecificUpdates.length - 1]
      : null;

    // Check for recent updates (within last 5 seconds)
    const now = Date.now();
    const hasRecentUpdates = fieldSpecificUpdates.some(
      update => now - update.timestamp < 5000
    );

    return {
      updates: fieldSpecificUpdates,
      latestUpdate,
      hasUpdates: fieldSpecificUpdates.length > 0,
      hasRecentUpdates
    };
  }, [fieldUpdates, fieldPath]);
}

/**
 * Hook to get updates for a section (path prefix)
 */
export function useSectionUpdates(pathPrefix: string): {
  updates: FieldUpdateEvent[];
  count: number;
  hasUpdates: boolean;
  hasRecentUpdates: boolean;
  latestUpdatesByPath: Map<string, FieldUpdateEvent>;
} {
  const { fieldUpdates } = useFieldUpdateContext();

  return useMemo(() => {
    // Get all updates for this section
    const sectionUpdates = groupUpdatesByPrefix(fieldUpdates, pathPrefix);

    // Get latest updates per path within section
    const latestUpdatesByPath = getLatestUpdatesPerPath(sectionUpdates);

    // Check for recent updates (within last 5 seconds)
    const now = Date.now();
    const hasRecentUpdates = sectionUpdates.some(
      update => now - update.timestamp < 5000
    );

    return {
      updates: sectionUpdates,
      count: sectionUpdates.length,
      hasUpdates: sectionUpdates.length > 0,
      hasRecentUpdates,
      latestUpdatesByPath
    };
  }, [fieldUpdates, pathPrefix]);
}

/**
 * Hook to check if field has been modified by user
 */
export function useFieldModificationStatus(fieldPath: string): {
  isModified: boolean;
  isUserModified: boolean;
  isExternallyModified: boolean;
  modificationSource: 'user' | 'firebase' | 'system' | 'ai' | null;
} {
  const { fieldUpdates } = useFieldUpdateContext();

  return useMemo(() => {
    const fieldSpecificUpdates = fieldUpdates.filter(
      update => update.path === fieldPath
    );

    if (fieldSpecificUpdates.length === 0) {
      return {
        isModified: false,
        isUserModified: false,
        isExternallyModified: false,
        modificationSource: null
      };
    }

    // Get latest update to determine current state
    const latestUpdate = fieldSpecificUpdates[fieldSpecificUpdates.length - 1];

    const isUserModified = fieldSpecificUpdates.some(update => update.source === 'user');
    const isExternallyModified = fieldSpecificUpdates.some(
      update => update.source === 'firebase' || update.source === 'system' || update.source === 'ai'
    );

    return {
      isModified: true,
      isUserModified,
      isExternallyModified,
      modificationSource: latestUpdate.source
    };
  }, [fieldUpdates, fieldPath]);
}

/**
 * Hook for updating multiple fields at once
 */
export function useMultiFieldUpdate(): (updates: Record<string, any>) => void {
  const { updateField } = useFieldUpdateContext();

  return useCallback((updates: Record<string, any>) => {
    // Apply all updates
    Object.entries(updates).forEach(([path, value]) => {
      updateField(path, value);
    });
  }, [updateField]);
}

/**
 * Hook for getting field value with automatic updates
 */
export function useFieldValue<T>(fieldPath: string): {
  value: T;
  setValue: (value: T) => void;
  hasUpdates: boolean;
  isModified: boolean;
} {
  const { formData, updateField } = useFieldUpdateContext();
  const { hasUpdates } = useFieldUpdates(fieldPath);
  const { isModified } = useFieldModificationStatus(fieldPath);

  // Get current value using lodash get
  const value = fieldPath.split('.').reduce((obj, key) => obj?.[key], formData) as T;

  const setValue = useCallback((newValue: T) => {
    updateField(fieldPath, newValue);
  }, [updateField, fieldPath]);

  return {
    value,
    setValue,
    hasUpdates,
    isModified
  };
}